import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostBinding,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IsActiveMatchOptions,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { ThemeService } from '../../services/theme.service';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subscription, catchError, filter, forkJoin, map, of, switchMap } from 'rxjs';

import { LeftNavigationPanelTranslations } from '../../i18n/left-navigation-panel';
import { TranslationService } from '../../services/translation.service';
import en from '../../i18n/en/left-navigation-panel';
import { ApplicationService } from '../../services/application.service';
import { AuthService } from '../../services/auth.service';
import { RootCvService } from '../../services/root-cv.service';
import { computeRootCvCompleteness } from '../../utils/root-cv-completeness.util';
import { LockedFeatureDirective } from '../../shared/directives/locked-feature.directive';

import { JobApplicationWizardServiceService } from '../../services/job-application-wizard-service.service';

@Component({
  selector: 'app-left-navigation-panel',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TooltipModule, LockedFeatureDirective],
  templateUrl: './left-navigation-panel.component.html',
  styleUrls: ['./left-navigation-panel.component.css'],
})
export class LeftNavigationPanelComponent implements OnInit, OnDestroy {
  @Input() filterPanelOpen = false;
  // chamba-ai#268: highlights the Root CV nav icon (not "My CVs" anymore -
  // that section is hidden from nav below, Root CV is the base now).
  @Input() highlightRootCv = false;
  @Output() toggleFilterPanel = new EventEmitter<void>();
  /** chamba-ai#433: icon-only rail (desktop). Toggled from the navbar. */
  @Input() @HostBinding('class.collapsed') collapsed = false;
  /** chamba-ai#433: rendered as the mobile off-canvas drawer - always
   * shows labels regardless of `collapsed`. */
  @Input() @HostBinding('class.mobile-drawer') mobile = false;
  /** Fired after any nav item is used, so the mobile drawer can close. */
  @Output() navigated = new EventEmitter<void>();
  @Output() openChat = new EventEmitter<number | null>();
  @Output() toggleGraph = new EventEmitter<void>();
  @Output() toggleSearchBar = new EventEmitter<void>();
  currentLanguage = 'en';
  translations: LeftNavigationPanelTranslations = en;
  isAdmin = false;
  /** New Application isn't a route link (it opens the wizard through its
   * service), so its active state is tracked by hand. */
  isApplyRoute = false;
  readonly exactMatch: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };
  private subscription = new Subscription();

  constructor(
    public themeService: ThemeService,
    public translationService: TranslationService,
    private router: Router,
    private jobApplicationWizardService: JobApplicationWizardServiceService,
    private applicationService: ApplicationService,
    private authService: AuthService,
    private rootCvService: RootCvService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const current = this.translationService.getLeftNavigationPanelTranslations();
    if (current) {
      this.translations = current;
    }
    // Keep in sync with global language changes
    this.translationService.onChange(() => {
      const next = this.translationService.getLeftNavigationPanelTranslations();
      if (next) {
        this.translations = next;
      }
    });

    // Subscribe to admin status changes
    this.subscription.add(
      this.authService.isAdmin$.subscribe((isAdmin) => {
        this.isAdmin = isAdmin;
      })
    );

    this.isApplyRoute = this.checkApplyRoute(this.router.url);
    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.isApplyRoute = this.checkApplyRoute(event.urlAfterRedirects);
        })
    );

    // Refresh user from server to get latest role
    this.authService.refreshUser().subscribe();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  get t(): LeftNavigationPanelTranslations {
    return this.translations;
  }
  /** Gates opening the wizard on having at least one Root CV that meets
   * the completeness threshold (utils/root-cv-completeness.util.ts -
   * ROOT_CV_COMPLETENESS_THRESHOLD, 80%) - there's no point starting an
   * application with nothing usable to tailor from. Zero Root CVs and
   * "has Root CVs but none complete enough" are treated the same: redirect
   * to /home/root-cv/start and orient the user with a toast, instead of
   * opening the wizard. */
  openWizard() {
    this.navigated.emit();
    this.hasCompleteRootCv().subscribe((hasCompleteRootCv) => {
      if (!hasCompleteRootCv) {
        this.messageService.add({
          severity: 'warn',
          summary: this.t.ROOT_CV_REQUIRED_TOAST_TITLE,
          detail: this.t.ROOT_CV_REQUIRED_TOAST_DETAIL,
        });
        this.router.navigateByUrl('/home/root-cv/start');
        return;
      }
      this.launchWizard();
    });
  }

  private checkApplyRoute(url: string): boolean {
    return url.startsWith('/home/wizard') || url.startsWith('/home/quick-application');
  }

  private hasCompleteRootCv() {
    return this.rootCvService.listRootCvs().pipe(
      switchMap((cvs) => {
        if (!cvs.length) return of(false);
        return forkJoin(
          cvs.map((cv) =>
            this.rootCvService.getRootCv(cv.language).pipe(
              map((version) => computeRootCvCompleteness(version.root_cv).isComplete),
              catchError(() => of(false))
            )
          )
        ).pipe(map((results) => results.some(Boolean)));
      }),
      catchError(() => of(false))
    );
  }

  private launchWizard(): void {
    // Check if we're currently in the wizard/quick-application flow and
    // need to close it properly first
    if (this.router.url.includes('/home/wizard') || this.router.url.includes('/home/quick-application')) {
      // Close current wizard properly to measure elapsed time
      this.jobApplicationWizardService.closeWizard();

      // Small delay to ensure proper cleanup, then open new wizard
      setTimeout(() => {
        this.applicationService.setCurrentApplicationId(null);
        this.jobApplicationWizardService.openWizard({});
      }, 100);
    } else {
      // Normal flow - just open new wizard
      this.applicationService.setCurrentApplicationId(null);
      this.jobApplicationWizardService.openWizard({});
    }
  }

  addNewApplication(): void {
    // Emit event to parent to open chat modal for a new application (null or custom logic)
    this.openChat.emit(null);
  }
  onToggleGraph(): void {
    this.toggleGraph.emit();
  }
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }
  toggleSearch(): void {
    if (this.router.url !== '/home') {
      this.router.navigateByUrl('/home');
    }
    this.toggleSearchBar.emit();
  }
  toggleFilter(): void {
    if (this.router.url !== '/home') {
      this.router.navigateByUrl('/home');
    }
    this.toggleFilterPanel.emit();
  }
}
